/* eslint-disable @typescript-eslint/no-explicit-any */
import { LocalDate, LocalDateTime } from 'js-joda';
import _ from 'lodash';

import {
  DragSelectionItem,
  SeatingTemplateInfo as SeatingTemplateInfoType,
  SeatingTemplateTable as SeatingTemplateTableType,
} from 'state/templateInfo';
import { TableAssignmentPriorityLevel } from 'generated/graphql';
import { dashboard } from 'types';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  GenerateTemplateRequest,
  SeatingTemplateApplyRequest,
  SeatingTemplateClearRequest,
  SetSeatingTemplateRequest,
} = protos;

/**
 * Fetches all entities to be able to render the template info screen.
 * @param {Number} templateId The id of the template.
 * @returns {Object} A redux TockApi action.
 */
export function fetchAll(templateId: number): ApiAction {
  return {
    TockApi: {
      batchProto: [
        {
          path: `/seating/template/${templateId}`,
          method: 'GET',
        },
        {
          path: `/seating/template/${templateId}/info`,
          method: 'GET',
        },
        {
          path: `/seating/template/${templateId}/table`,
          method: 'GET',
        },
        {
          path: `/seating/template/${templateId}/table/combinations`,
          method: 'GET',
        },
        {
          path: '/ticket/type',
          method: 'GET',
        },
        {
          path: '/ticket/option',
          method: 'GET',
        },
        {
          path: '/service',
          method: 'GET',
        },
      ],
      type: AppConstants.TEMPLATE_INFO_FETCH_ALL,
    },
  };
}

function cleanUpTemplateInfo(templateInfo: SeatingTemplateInfoType) {
  const templateInfoCleaned = templateInfo.id < 0 ? _.omit(templateInfo, 'id') : templateInfo;
  if (templateInfo.variety !== `COMMUNAL_TABLE`) {
    return {
      ...templateInfoCleaned,
      isEvensOnly: false,
      allowsStragglers: false,
    };
  }
  return templateInfoCleaned;
}

function cleanUpTemplateTable(templateTable: SeatingTemplateTableType) {
  return templateTable.id < 0 ? _.omit(templateTable, 'id') : templateTable;
}

function cleanSeatingTemplate(template: dashboard.SeatingTemplate) {
  const modifiedTemplate = _.cloneDeep(template);
  _.forEach(modifiedTemplate.tableGroup, (tableGroup) => {
    if (tableGroup.id && tableGroup.id < 0) {
      delete tableGroup.id;
    }
  });
  return modifiedTemplate;
}

/**
 * Saves all template entities.
 * @param {Object} template The template.
 * @param {Array} templateInfos The seating template info at each entry.
 * @param {Array} templateTables The seating template table at each entry.
 * @returns {Object} A redux TockApi action.
 */
export function saveAll(
  template: dashboard.SeatingTemplate,
  templateInfos: SeatingTemplateInfoType[],
  templateTables: SeatingTemplateTableType[],
  templateTableCombinations: dashboard.SeatingTemplateTableCombination[]
): ApiAction {
  const request = SetSeatingTemplateRequest.fromObject({
    seatingTemplate: cleanSeatingTemplate(template),
    seatingTemplateInfo: _.map(
      _.filter(templateInfos, (templateInfo) => !templateInfo._deleted),
      cleanUpTemplateInfo
    ),
    seatingTemplateTable: _.map(
      _.filter(templateTables, (templateTable) => !templateTable._deleted),
      cleanUpTemplateTable
    ),
    seatingTemplateTableCombination: templateTableCombinations,
  });
  return {
    TockApi: {
      putProto: '/seating/template/save',
      request: request,
      type: AppConstants.TEMPLATE_INFO_SAVE_ALL,
      context: {
        template,
        templateInfos,
        templateTables,
        templateTableCombinations,
      },
    },
  };
}

/**
 * Updates the filter ui.
 * @param {Object} uiState The partial ui state.
 * @returns {Object} A redux action.
 */
export function updateFilterUiState(uiState: any): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_UPDATE_FILTER_DONE,
    payload: uiState,
  };
}

/**
 * Add the template with the given parameters
 * @param {Object} templateInfoBase A complete description of a template info, sans templateId and price
 * @returns {Object} A redux action.
 */
export function addCompleteTemplateInfoUiState(
  templateInfoBase: Partial<dashboard.SeatingTemplateInfo>
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_ADD_COMPLETE_TEMPLATE_INFO,
    payload: templateInfoBase,
  };
}

/**
 * Add the template info at the given time.
 * @param {String} time The partial ui state.
 * @returns {Object} A redux action.
 */
export function addTemplateInfoUiState(time: any): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_ADD_TEMPLATE_INFO_DONE,
    payload: time,
  };
}

/**
 * Updates the template info.
 * @param {Object} partialTemplateInfo The partial template info.
 * @returns {Object} A redux action.
 */
export function updateTemplateInfoUiState(
  partialTemplateInfo: Partial<dashboard.SeatingTemplateInfo>
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_UPDATE_TEMPLATE_INFO_DONE,
    payload: partialTemplateInfo,
  };
}

/**
 * updates info in batch based on ids
 *
 * @param {Number[]} templateInfoIds IDs of template infos to remove
 * @param {Object} update object that has the update data for all
 */
export function batchUpdateTemplateInfoUiState(
  templateInfoIds: number[],
  update: Partial<dashboard.SeatingTemplateInfo>
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_BATCH_UPDATE_TEMPLATE_INFO_DONE,
    payload: { templateInfoIds, update },
  };
}

/**
 * Deletes the template info.
 * @param {Number} templateInfoId The id of the template info.
 * @returns {Object} A redux action.
 */
export function deleteTemplateInfoUiState(templateInfoId: number): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_DELETE_TEMPLATE_INFO_DONE,
    payload: templateInfoId,
  };
}

/**
 * Deletes all given template info IDs from the template currently being edited in template
 * redux state.
 *
 * @param {Number[]} templateInfoIds IDs of template infos to remove
 */
export function batchRemove(templateInfoIds: number[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_BATCH_REMOVE_DONE,
    payload: templateInfoIds,
  };
}

/**
 * Places holds on all template infos. For communal tables, holds all seats.
 *
 * @param {Number[]} templateInfoIds IDs of template infos to remove
 */
export function placeHolds(templateInfoIds: number[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_PLACE_HOLDS_DONE,
    payload: templateInfoIds,
  };
}

/**
 * removes holds on all template infos..
 *
 * @param {Number[]} templateInfoIds IDs of template infos to remove
 */
export function removeHolds(templateInfoIds: number[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_REMOVE_HOLDS_DONE,
    payload: templateInfoIds,
  };
}

/**
 * Resets all template infos in the template store to the given array. No
 * sanity-checking is done on this value, so make sure it's a valid seating template
 * info list before sending it.
 */
export function resetTemplateInfos(
  templateInfos: dashboard.ISeatingTemplateInfo[]
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_RESET_TEMPLATE_INFOS,
    payload: templateInfos,
  };
}

/**
 * Updates the template.
 * @param {Object} partialTemplate The partial template.
 * @returns {Object} A redux action.
 */
export function updateTemplateUiState(partialTemplate: any): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_UPDATE_TEMPLATE_DONE,
    payload: partialTemplate,
  };
}

export function setFullyFlexedTicketType(
  tableId: number | Set<number>,
  ticketTypeId: number | Set<number>
): BaseTockAction {
  const tableIds: Set<number> = _.isNumber(tableId) ? new Set([tableId]) : tableId;
  const ticketTypeIds: Set<number> = _.isNumber(ticketTypeId)
    ? new Set([ticketTypeId])
    : ticketTypeId;
  return {
    type: AppConstants.TEMPLATE_INFO_SET_FULLY_FLEXED_TICKET_TYPE,
    payload: {
      tableIds,
      ticketTypeIds,
    },
  };
}

export function addFullyFlexedTicketType(
  tableId: number | Set<number>,
  ticketTypeId: number
): BaseTockAction {
  const tableIds: Set<number> = _.isNumber(tableId) ? new Set([tableId]) : tableId;
  return {
    type: AppConstants.TEMPLATE_INFO_ADD_FULLY_FLEXED_TICKET_TYPE,
    payload: {
      tableIds,
      ticketTypeId,
    },
  };
}

export function removeFullyFlexedTicketType(
  tableId: number | Set<number>,
  ticketTypeId: number
): BaseTockAction {
  const tableIds: Set<number> = _.isNumber(tableId) ? new Set([tableId]) : tableId;
  return {
    type: AppConstants.TEMPLATE_INFO_REMOVE_FULLY_FLEXED_TICKET_TYPE,
    payload: {
      tableIds,
      ticketTypeId,
    },
  };
}

export function setTablesAreFullyFlexed(
  tableIds: number[],
  { isFullyFlexed }: { isFullyFlexed: boolean },
  ticketTypeId?: number
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLES_FULLY_FLEXED,
    payload: {
      tableIds,
      ticketTypeId,
      isFullyFlexed,
    },
  };
}

export function setTableOfferedOnline(tableId: number, offeredOnline: boolean): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLE_OFFERED_ONLINE,
    payload: {
      tableId,
      offeredOnline,
    },
  };
}

export function setAssignmentPriorityLevel(
  tableId: number,
  priorityLevel: TableAssignmentPriorityLevel
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_ASSIGNMENT_PRIORITY_LEVEL,
    payload: {
      tableId,
      priorityLevel,
    },
  };
}

export function setTableCombinationOfferedOnline(
  tableCombinationId: number,
  offeredOnline: boolean
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLE_COMBINATION_OFFERED_ONLINE,
    payload: {
      tableCombinationId,
      offeredOnline,
    },
  };
}

export function setTableCombinationAssignmentPriorityLevel(
  tableCombinationId: number,
  priorityLevel: dashboard.TableAssignmentPriorityLevelProtoEnum
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLE_COMBINATION_ASSIGNMENT_PRIORITY_LEVEL,
    payload: {
      tableCombinationId,
      priorityLevel,
    },
  };
}

export function addFlexAvailability(
  tableIds: number[],
  defaultTicketTypeId?: number
): BaseTockAction {
  return setTablesAreFullyFlexed(tableIds, { isFullyFlexed: true }, defaultTicketTypeId);
}

export function removeFlexAvailability(tableIds: number[]): BaseTockAction {
  return setTablesAreFullyFlexed(tableIds, { isFullyFlexed: false });
}

/**
 * Resets all tables in the template store to the given array. No
 * sanity-checking is done on this value, so make sure it's a valid table
 * list before sending it.
 */
export function resetTables(tables: dashboard.ITable[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_RESET_TABLES,
    payload: tables,
  };
}

/**
 * Resets all tables combinations in the template store to the given array. No
 * sanity-checking is done on this value, so make sure it's a valid table combination
 * list before sending it.
 */
export function resetTableCombinations(tables: dashboard.ITableCombination[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_RESET_TABLE_COMBINATIONS,
    payload: tables,
  };
}

/**
 * Resets all template tables in the template store to the given array. No
 * sanity-checking is done on this value, so make sure it's a valid seating template
 * template list before sending it.
 */
export function resetTemplateTables(
  templateTables: dashboard.ISeatingTemplateTable[]
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_RESET_TEMPLATE_TABLES,
    payload: templateTables,
  };
}

/**
 * Resets all template table combinations in the template store to the given array. No
 * sanity-checking is done on this value, so make sure it's a valid seating template
 * table combination list before sending it.
 */
export function resetTemplateTableCombinations(
  templateTableCombinations: dashboard.ISeatingTemplateTableCombination[]
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_RESET_TEMPLATE_TABLE_COMBINATIONS,
    payload: templateTableCombinations,
  };
}

export function updateBlocks(
  tableIds: number[],
  start: LocalDateTime,
  end: LocalDateTime,
  mode: 'ADD' | 'REMOVE'
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLES_BLOCKS,
    payload: {
      tableIds,
      start,
      end,
      mode,
    },
  };
}

export function updateTicketTypeBookableRanges(
  tableIds: number[],
  start: LocalDateTime,
  end: LocalDateTime,
  mode: 'ADD' | 'REMOVE',
  ticketTypeId: number
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLES_BOOKABLE_RANGE,
    payload: {
      tableIds,
      start,
      end,
      mode,
      ticketTypeId,
    },
  };
}

export function updateTicketTypeBookableRangesForCombos(
  tableCombinationIds: number[],
  start: LocalDateTime,
  end: LocalDateTime,
  mode: 'ADD' | 'REMOVE',
  ticketTypeId: number
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLE_COMBOS_BOOKABLE_RANGE,
    payload: {
      tableCombinationIds,
      start,
      end,
      mode,
      ticketTypeId,
    },
  };
}

export function updateTicketTypeTableRangeConditions(
  tableIds: number[],
  mode: 'ADD' | 'REMOVE',
  ticketTypeId: number,
  effectiveAtMinutesBefore: number | undefined
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLE_RANGE_CONDITIONS,
    payload: {
      tableIds,
      effectiveAtMinutesBefore,
      mode,
      ticketTypeId,
    },
  };
}

export function removeExperienceOptimizationFromTable(
  tableId: number,
  ticketTypeId: number
): BaseTockAction {
  return removeExperienceOptimizationFromTables([tableId], ticketTypeId);
}

export function removeExperienceOptimizationFromTables(
  tableIds: number[],
  ticketTypeId: number
): BaseTockAction {
  return updateTicketTypeTableRangeConditions(tableIds, 'ADD', ticketTypeId, undefined);
}

export function clearTicketTypeTableRangeConditions(tableIds: number[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_CLEAR_TABLE_RANGE_CONDITIONS,
    payload: {
      tableIds,
    },
  };
}

export function addTableSizeConfigToTables(
  tableIds: number[],
  minPurchaseSize: number,
  effectiveAtMinutesBeforeService: number
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_ADD_TEMPLATE_SIZE_CONFIG,
    payload: {
      tableIds,
      minPurchaseSize,
      timeBeforeServiceMinutes: effectiveAtMinutesBeforeService,
    },
  };
}

export function setTableSizeConfigToTables(
  tableIds: number[],
  sizeConfigs: dashboard.SeatingTemplateTableSize[]
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLE_SIZE_CONFIGS,
    payload: {
      tableIds,
      sizeConfigs,
    },
  };
}

export function clearAllTableSizeConfigsFromTables(tableIds: number[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_CLEAR_TABLE_SIZE_CONFIGS,
    payload: {
      tableIds,
    },
  };
}

export function removeTableSizeConfigFromTables(
  tableIds: number[],
  effectiveAtMinutesBefore: number,
  minPurchaseSize: number
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_REMOVE_TABLE_SIZE_CONFIG,
    payload: {
      tableIds,
      effectiveAtMinutesBefore,
      minPurchaseSize,
    },
  };
}

export function setExperienceOnTableCombination(
  tableCombinationId: number,
  ticketTypeId: number
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_EXPERIENCE_ON_TABLE_COMBOS,
    payload: {
      tableCombinationId,
      ticketTypeId,
    },
  };
}

export function removeExperienceOnTableCombination(
  tableCombinationId: number,
  ticketTypeId: number
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_REMOVE_EXPERIENCE_ON_TABLE_COMBOS,
    payload: {
      tableCombinationId,
      ticketTypeId,
    },
  };
}

export function clearAllExperiencesOnTableCombination(tableCombinationId: number): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_CLEAR_EXPERIENCES_ON_TABLE_COMBO,
    payload: {
      tableCombinationId,
    },
  };
}

export function updateHolds(
  tableIds: number[],
  start: LocalDateTime,
  end: LocalDateTime,
  mode: 'ADD' | 'REMOVE'
): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLES_HOLDS,
    payload: {
      tableIds,
      start,
      end,
      mode,
    },
  };
}

export function addTableGroup(groupedTableIds: number[]): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_TABLE_GROUP_ADD_DONE,
    payload: {
      groupedTableIds,
    },
  };
}

export function removeTableGroup(tableGroupId: number): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_UI_STATE_TABLE_GROUP_REMOVE_DONE,
    payload: {
      tableGroupId,
    },
  };
}

export function updateAutoRelease(tableIds: number[], enabled: boolean): BaseTockAction {
  return {
    type: AppConstants.TEMPLATE_INFO_SET_TABLES_AUTO_RELEASE_ENABLED,
    payload: {
      tableIds,
      enabled,
    },
  };
}

export function clearApplyTemplateWarnings(): BaseTockAction {
  return {
    type: AppConstants.CLEAR_APPLY_TEMPLATE_WARNINGS,
    payload: null,
  };
}

export function applyTemplate(request: Partial<dashboard.SeatingTemplateApplyRequest>): ApiAction {
  return {
    TockApi: {
      postProto: '/seating/template/apply',
      type: AppConstants.APPLY_TEMPLATE,
      request: SeatingTemplateApplyRequest.fromObject(request),
      toast: 'Blueprint applied on the calendar',
    },
  };
}

export function applyTemplateCheck(
  request: Partial<dashboard.SeatingTemplateApplyRequest>
): ApiAction {
  return {
    TockApi: {
      postProto: '/seating/template/apply-check',
      type: AppConstants.APPLY_CHECK_TEMPLATE,
      request: SeatingTemplateApplyRequest.fromObject(request),
    },
  };
}

export function generateTemplate(request: any): ApiAction {
  return {
    TockApi: {
      postProto: '/seating/template/generate',
      type: AppConstants.GENERATE_TEMPLATE,
      request: GenerateTemplateRequest.fromObject(request),
      toast: 'Blueprint generated',
    },
  };
}

export function clearTemplate(
  startDate: LocalDate,
  endDate: LocalDate,
  serviceType: number,
  selectedWeekdays: string[]
): ApiAction {
  const dates: string[] = [];

  for (let m = startDate; m.isBefore(endDate) || m.isEqual(endDate); m = m.plusDays(1)) {
    if (_.includes(selectedWeekdays, m.dayOfWeek().toString())) {
      dates.push(m.toString());
    }
  }

  const request = SeatingTemplateClearRequest.fromObject({
    date: dates,
    serviceType,
  });

  return {
    TockApi: {
      postProto: '/seating/template/clear',
      type: AppConstants.CLEAR_TEMPLATE,
      request,
      toast: 'Available reservations removed from selected dates',
    },
  };
}

export function setCurrentDragInventorySquare(selection: DragSelectionItem | null): BaseTockAction {
  return {
    payload: selection,
    type: AppConstants.TEMPLATE_INFO_DRAG_SELECT_CURRENT_INVENTORY_SQUARE,
  };
}

export function setStartDragInventorySquare(selection: DragSelectionItem | null): BaseTockAction {
  return {
    payload: selection,
    type: AppConstants.TEMPLATE_INFO_DRAG_SELECT_START_INVENTORY_SQUARE,
  };
}

export function toggleDragSelectStatus(selection: boolean): BaseTockAction {
  return {
    payload: selection,
    type: AppConstants.TEMPLATE_INFO_DRAG_SELECT_TOGGLE_STATUS,
  };
}

export function selectDynamicOptimizationTime(timeBeforeServiceMinutes: number): BaseTockAction {
  return {
    payload: timeBeforeServiceMinutes,
    type: AppConstants.TEMPLATE_INFO_SELECT_DYNAMIC_OPTIMIZATION_TIME,
  };
}

export function selectStartingScheduleDynamicOptimizationTime(): BaseTockAction {
  return {
    payload: Number.MAX_SAFE_INTEGER,
    type: AppConstants.TEMPLATE_INFO_SELECT_DYNAMIC_OPTIMIZATION_TIME,
  };
}

export function clearDynamicOptimizationTime(): BaseTockAction {
  return {
    payload: null,
    type: AppConstants.TEMPLATE_INFO_CLEAR_DYNAMIC_OPTIMIZATION_TIME,
  };
}

export interface TemplateInfoActions {
  addCompleteTemplateInfoUiState(templateInfoBase: Partial<dashboard.SeatingTemplateInfo>): void;
  deleteTemplateInfoUiState(templateId: number): void;
  fetchAll(templateId: number): Promise<unknown>;
  saveAll(
    template: dashboard.SeatingTemplate,
    templateInfos: dashboard.SeatingTemplateInfo[]
  ): Promise<unknown>;
  updateFilterUiState(newState: { showPrice: boolean }): void;
  updateTemplateUiState(newUiState: any): void;
  updateTemplateInfoUiState(newTemplateInfoUiState: any): void;
}
